import { Component, Inject, PLATFORM_ID, type OnInit } from '@angular/core';
import { isPlatformBrowser, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { map } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';

import { NotificationComponent } from '../../shared/components/notification/notification.component';

import { type AccountDetail } from '../../shared/models/account-detail';
import { type PhoneInfo } from '../../shared/models/phone-info';
import { StorageEnum } from '../../shared/enums/storage.enum';
import { type UserProfile } from '../../shared/models/user-profile';
import { type EmailInfo } from '../../shared/models/email-info';
import { Nav } from '../../shared/enums/nav.enum';
import { PhoneType } from '../../shared/enums/phonetype.enum';
import { NotificationType } from '../../shared/enums/notificationtype.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../shared/services/util.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../shared/services/storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../shared/services/notifications.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../shared/services/cms.service';
import { MatInputModule } from '@angular/material/input';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { DomSanitizer } from '@angular/platform-browser';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountService } from '../../shared/services/account.service';
import { accountNicknameValidator } from '../../shared/validators/nickName.validator';
import { MatTooltip } from '@angular/material/tooltip';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PageHeader } from '../../shared/models/page-header.model';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-overview',
  imports: [
    MatCardModule,
    MatButtonModule,
    UpperCasePipe,
    TitleCasePipe,
    RouterModule,
    MatFormFieldModule,
    NotificationComponent,
    MatInputModule,
    ReactiveFormsModule,
    MatTooltip,
    PageHeaderComponent,
    RouterModule,
  ],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
})
export class OverviewComponent implements OnInit {
  isBrowser = false;
  pageReady = false;
  formErrorMessage = '';
  accountDetail: AccountDetail;
  loginRes: UserProfile;
  accountData: any = {};
  pageData: any = {};
  notification = '';
  pageName = 'Overview';
  pageLabels: any = {};
  nickNameForm!: FormGroup;
  pageHeader = new PageHeader();

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notifi: NotificationsService,
    private readonly cms: CmsService,
    protected sanitizer: DomSanitizer,
    private readonly formBuilder: FormBuilder,
    private readonly account: AccountService,
    private readonly seoS: SeoService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.loginRes = this.storage.getSession(StorageEnum.LoginResponse);
    this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
  }

  ngOnInit(): void {
    this.cms
      .getContent('oas-manage-account', 'OAS Manage Account')
      .pipe(
        map((data) => {
          this.pageLabels = data;
          this.pageHeader.Message = this.pageLabels.header;
          this.seoS.setSeoTags(data.seo);
        }),
      )
      .subscribe((data) => {
        if (this.isBrowser) {
          this.nickNameForm = this.formBuilder.group({
            nickName: [this.loginRes.Accounts[0].NickName, [accountNicknameValidator()]],
          });
          this.setPageData();
          this.setAccountData();
          this.checkForNotificationMessage();
          this.pageReady = true;
          this.utils.hideSpinner();
        }
      });
  }

  setAccountData(): void {
    // contact data
    this.accountData.Email =
      this.accountDetail.Person?.EmailAddresses?.find((email: EmailInfo) => {
        return email.IsPrimary;
      })?.Email ?? 'Please add an email address.';

    if (this.accountDetail?.Person?.PhoneNumbers && this.accountDetail.Person.PhoneNumbers.length > 0) {
      this.accountData.Phone = this.accountDetail.Person?.PhoneNumbers?.find((phone: PhoneInfo) => {
        return phone.IsActive && phone.IsPrimary;
      })?.Number;

      if (!this.accountData.Phone) {
        this.accountData.Phone = this.accountDetail.Person?.PhoneNumbers[0].Number;
      }
    } else {
      this.accountData.Phone = 'Add a phone number';
    }

    // address data
    if (this.pageData.HasAddress) {
      this.accountData.MailingAddress = {
        Address1: this.accountDetail.Person?.BillingAddress?.Address1,
        City: this.accountDetail.Person?.BillingAddress?.City,
        State: this.accountDetail.Person?.BillingAddress?.State,
        PostalCode: this.accountDetail.Person?.BillingAddress?.PostalCode,
      };
    }

    // login data
    this.accountData.UserName = this.loginRes.UserName;

    // refer-a-friend data
    this.pageData.referFriendURL =
      'https://www.facebook.com/sharer/sharer.php?u=' + encodeURI(this.accountDetail.ReferFriendCode);
  }

  openFB(): void {
    const fbUrl = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURI(this.accountDetail.ReferFriendCode);
    window.open(fbUrl, 'mypopuptitle', 'width=600,height=400');
  }

  setPageData(): void {
    // Plan Data
    if (this.accountDetail.ServiceAddressCount === 1) {
      this.pageData.PlanLink = Nav.PlanDetails;
      this.pageData.PlanAria = 'Edit Service and Plan Details';
    } else {
      this.pageData.PlanLink = Nav.MultipleSAs;
      this.pageData.PlanAria = 'View all service address details';
    }

    // Login Data
    this.pageData.LoginLink = Nav.UpdateLoginInfo;
    this.pageData.LoginAria = 'Edit Login Information';

    // Mailing Data
    if (this.accountDetail.Person?.BillingAddress?.Address1) {
      this.pageData.HasAddress = true;
      this.pageData.MailingLink = Nav.ChangeMailing;
      this.pageData.MailingAria = 'Edit your Change your mailing address';
      this.pageData.MailingCta = 'Edit';
    } else {
      this.pageData.HasAddress = false;
      this.pageData.MailingLink = Nav.AddMailing;
      this.pageData.MailingAria = 'Add a mailing address';
      this.pageData.MailingCta = 'Add';
    }

    // Contact Data
    const email = this.accountDetail.Person?.EmailAddresses?.find((email: EmailInfo) => {
      return email.IsPrimary;
    });
    if (email && email.Type?.toUpperCase() === 'UNDELIVERABLE EMAIL') {
      this.pageData.EmailUndeliverable = true;
    } else {
      this.pageData.EmailUndeliverable = false;
    }

    if (this.accountDetail?.Person?.PhoneNumbers && this.accountDetail.Person.PhoneNumbers.length > 0) {
      this.pageData.PhoneLabel = this.accountDetail.Person.PhoneNumbers.length > 1 ? 'Phone Numbers' : 'Phone Number';
      this.pageData.MorePhoneNumbers =
        this.accountDetail.Person.PhoneNumbers.length > 1
          ? `(${this.accountDetail.Person.PhoneNumbers.length - 1} more)`
          : '';
      const phone = this.accountDetail.Person?.PhoneNumbers?.find((phone: PhoneInfo) => {
        return phone.IsActive && phone.IsPrimary;
      });
      if (phone) {
        this.pageData.PhoneUnconfirmed = phone.Type === PhoneType.Unconfirmed;
      } else {
        this.pageData.PhoneUnconfirmed = false;
      }
    } else {
      this.pageData.PhoneLabel = 'Phone Number';
    }

    this.pageData.ContactAria = 'Edit your contact information';
    this.pageData.ContactLink = Nav.ContactInfo;

    this.pageData.AlertsAria = 'Edit your alerts';
    this.pageData.AlertsLink = Nav.Alerts;

    // Refer-A-Friend Data
    this.pageData.RAFLink = this.pageLabels.getReferAFriendCodeLink.value.data.url;
    this.pageData.RAFLinkText = this.pageLabels.getReferAFriendCodeLink.value.data.linkText;
    this.pageData.RAFLinkAriaLabel = this.pageLabels.getReferAFriendCodeLink.value.data.ariaLabel;
  }

  checkForPasswordChange(): boolean {
    if (this.router.url.includes('/manage-account/overview/change-password')) {
      return true;
    } else {
      return false;
    }
  }

  checkForNotificationMessage(): void {
    const queryParams = this.route.snapshot.queryParams;
    const successMessage = queryParams['successMessage'];
    const errorMessage = queryParams['errorMessage'];
    if (!this.utils.isNullOrUndefined(successMessage)) {
      this.notification = this.pageLabels[successMessage];
      if (this.notification !== '') {
        this.notifi.addNotification(this.notification, NotificationType.Postnotification, this.pageName);
      }
    }
    if (!this.utils.isNullOrUndefined(errorMessage)) {
      this.notification = this.pageLabels[errorMessage];
      if (this.notification !== '') {
        this.notifi.addNotification(this.notification, NotificationType.Alert, this.pageName);
      }
    }
  }

  copyText(inputValue: any): void {
    navigator.clipboard.writeText(inputValue).catch(() => {
      console.error('Unable to copy text');
    });
  }

  saveNickName(): void {
    if (this.nickNameForm.valid) {
      this.notifi.clearAllNotifications();
      this.utils.showSpinner();
      const nickName = this.nickNameForm.get('nickName')?.value;
      this.account.updateAccountNickName(this.accountDetail.AccountNumber, nickName).subscribe({
        error: (e) => {
          this.notification = this.pageLabels.changeAccountNickNameErrorText;
          this.notifi.addNotification(this.notification, NotificationType.Alert, this.pageName);
          this.utils.hideSpinner();
        },
        next: (data: any) => {
          // Update Storage
          if (this.loginRes.Accounts[0].NickName) {
            this.notification = this.pageLabels.changeAccountNickNameSuccessText;
          } else {
            this.notification = this.pageLabels.addAccountNickNameSuccessText;
          }
          this.notifi.addNotification(this.notification, NotificationType.Postnotification, this.pageName);
          this.loginRes.Accounts[0].NickName = nickName;
          this.storage.setSession(StorageEnum.LoginResponse, this.loginRes);
          this.utils.hideSpinner();
        },
      });
    }
  }

  onCancelNickNameClick(): void {
    this.nickNameForm.get('nickName')?.setValue(this.loginRes.Accounts[0].NickName);
  }
}
